'use client'

import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import Link from 'next/link'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { NAV_LINKS } from '@/lib/nav'
import { SOCIAL, ORG } from '@/lib/site'
import { cn } from '@/lib/cn'

const SOCIALS = [
  { label: 'LinkedIn', url: SOCIAL.linkedin.url },
  { label: 'GitHub', url: SOCIAL.github.url },
  { label: 'X', url: SOCIAL.x.url },
]

export function MobileMenu() {
  const [open, setOpen] = useState(false)
  const pathname = usePathname()
  const close = () => setOpen(false)

  // Lock background scroll + close on Escape while open.
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className="md:hidden">
      <button
        type="button"
        aria-label="Open menu"
        aria-expanded={open}
        aria-controls="mobile-menu"
        onClick={() => setOpen(true)}
        className="flex h-11 w-11 flex-col items-center justify-center gap-1.25 rounded-[10px] border-2 border-ink bg-white"
      >
        <span className="h-[2.5px] w-5.5 bg-ink" />
        <span className="h-[2.5px] w-5.5 bg-ink" />
        <span className="h-[2.5px] w-5.5 bg-ink" />
      </button>

      {open &&
        createPortal(
          <div id="mobile-menu" className="fixed inset-0 z-120 flex flex-col bg-ink text-white">
          {/* Top bar — logo + close */}
          <div className="flex items-center justify-between px-6 py-4.5">
            <Link
              href="/"
              onClick={close}
              className="flex items-center gap-2.75 font-display text-[23px] font-extrabold tracking-[-0.03em] text-white"
            >
              <Image
                src="/logo.png"
                alt=""
                width={38}
                height={38}
                className="h-9.5 w-9.5 flex-none rounded-[11px] border-2 border-white/25"
              />
              Ryens
            </Link>
            <button
              type="button"
              aria-label="Close menu"
              onClick={close}
              className="flex h-11 w-11 items-center justify-center rounded-[10px] border-2 border-white/25 text-[26px] leading-none text-white"
            >
              &times;
            </button>
          </div>

          {/* Links + CTA */}
          <nav className="flex flex-1 flex-col justify-center px-6">
            {NAV_LINKS.map((l, i) => {
              const active = pathname === l.href
              return (
                <Link
                  key={l.href}
                  href={l.href}
                  onClick={close}
                  aria-current={active ? 'page' : undefined}
                  style={{ animationDelay: `${0.05 + i * 0.05}s` }}
                  className={cn(
                    'menu-row flex items-center justify-between border-b border-white/10 py-3.5 font-display text-[28px] font-semibold',
                    active ? 'text-coral' : 'text-white',
                  )}
                >
                  {l.label}
                  <span aria-hidden className={cn('text-xl', active ? 'text-coral' : 'text-white/30')}>
                    →
                  </span>
                </Link>
              )
            })}

            <Link
              href="/contact"
              onClick={close}
              style={{ animationDelay: `${0.05 + NAV_LINKS.length * 0.05}s` }}
              className="menu-row mt-7 inline-flex items-center justify-center gap-2 rounded-xl border-2 border-ink bg-coral px-5 py-3.5 font-display text-[17px] font-semibold text-ink shadow-[4px_4px_0_rgba(0,0,0,0.4)]"
            >
              Start your MVP <span aria-hidden>→</span>
            </Link>
          </nav>

          {/* Contact + social */}
          <div className="px-6 pb-9 pt-4">
            <a
              href={`mailto:${ORG.email}`}
              onClick={close}
              className="inline-block py-1 font-mono text-sm text-white/70"
            >
              {ORG.email}
            </a>
            <div className="mt-1 flex gap-5">
              {SOCIALS.map((s) => (
                <a
                  key={s.label}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="py-1 font-mono text-[13px] uppercase tracking-[0.04em] text-white/60"
                >
                  {s.label}
                </a>
              ))}
            </div>
          </div>
          </div>,
          document.body,
        )}
    </div>
  )
}
